import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ToastService } from 'src/app/services/general/toast.service';
import { environment } from 'src/environments/environment';
import { AcceptService, DropzoneComponent, DropzoneService, FileInputDirective } from "@ngx-dropzone/cdk";

interface FileWithProgress {
  file: File;
  id: string;
  name: string;
  progress: {
    data: {
      percentage: number;
      speedHuman?: string;
      etaHuman?: string;
    }
  };
  uploading: boolean;
  uploaded: boolean;
  response?: any;
}

@Component({
  selector: 'app-file-attachment',
  templateUrl: './file-attachment.component.html',
  styleUrls: ['./file-attachment.component.scss'],
  imports: [
    FileInputDirective,
    DropzoneComponent,
    TranslateModule,
    CommonModule,
    MatProgressBarModule,
    MatIconModule,
    MatButtonModule
  ],
  providers: [DropzoneService, AcceptService],
  standalone: true,
})
export class FileAttachmentComponent implements OnInit {

  @Input() data: any;
  @Output() attachElement: EventEmitter<any> = new EventEmitter();

  loading: boolean = false;
  dragOver: boolean = false;

  url = environment.apiUrl + 'upload/tmp-proposal-file';
  files: FileWithProgress[] = [];
  uploadedFiles: any[] = [];

  maxFileSize: number = 6000000; // 6MB default
  maxUploads: number = 50;
  humanizeFileSize: number = 0;
  concurrency: number = 1;

  selectedFiles: File[] | null = null;

  constructor(
    private translateService: TranslateService,
    private toast: ToastService,
    private http: HttpClient
  ) {
    this.humanizeFileSize = this.maxFileSize / (1024 * 1024);
  }

  ngOnInit(): void {
    if (typeof this.data.uploadUrl === 'string') {
      this.url = environment.apiUrl + this.data.uploadUrl;
    }

    if (!this.data.acceptedTypes) {
      this.data.acceptedTypes = '*/*';
    }

    if (this.data.maxUploadsFile) {
      this.maxUploads = this.data.maxUploadsFile;
    }
    if (this.data.maxFileSize) {
      this.maxFileSize = this.data.maxFileSize;
      this.humanizeFileSize = this.maxFileSize / (1024 * 1024);
    }
  }

  onFilesSelected(files: File[] | File | null): void {
    if (!files) return;

    const fileArray = Array.isArray(files) ? files : [files];

    // Validate file size
    const oversizedFiles = fileArray.filter(f => f.size > this.maxFileSize);
    if (oversizedFiles.length > 0) {
      this.toast.alert(this.translateService.instant("components.attachment.file-attachment.alert-files-requirements"));
      return;
    }

    // Check max uploads
    if (this.files.length + fileArray.length > this.maxUploads) {
      this.toast.alert(this.translateService.instant("components.attachment.file-attachment.alert-max-uploads"));
      return;
    }

    // Add files to queue
    fileArray.forEach(file => {
      const fileWithProgress: FileWithProgress = {
        file: file,
        id: this.generateId(),
        name: file.name,
        progress: {
          data: {
            percentage: 0
          }
        },
        uploading: false,
        uploaded: false
      };

      this.files.push(fileWithProgress);
    });

    // Start upload
    this.startUpload();
  }

  onDragOver(isDragOver: boolean): void {
    this.dragOver = isDragOver;
  }

  startUpload(): void {
    this.loading = true;

    const filesToUpload = this.files.filter(f => !f.uploading && !f.uploaded);

    filesToUpload.forEach(fileWithProgress => {
      this.uploadFile(fileWithProgress);
    });
  }

  uploadFile(fileWithProgress: FileWithProgress): void {
    fileWithProgress.uploading = true;

    const formData = new FormData();
    formData.append('file', fileWithProgress.file);

    const startTime = Date.now();
    
    let currentUrl = this.url;
    if (typeof this.data.uploadUrl === 'function') {
      currentUrl = environment.apiUrl + this.data.uploadUrl();
    }

    this.http.post(currentUrl, formData, {
      reportProgress: true,
      observe: 'events',
      withCredentials: true
    }).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          const percentage = Math.round(100 * event.loaded / (event.total || 1));
          fileWithProgress.progress.data.percentage = percentage;

          // Calculate speed and ETA
          const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
          const uploadedMB = event.loaded / (1024 * 1024);
          const speed = uploadedMB / elapsedTime; // MB/s
          const remainingMB = ((event.total || 0) - event.loaded) / (1024 * 1024);
          const eta = remainingMB / speed; // seconds

          fileWithProgress.progress.data.speedHuman = `${speed.toFixed(2)} MB/s`;
          fileWithProgress.progress.data.etaHuman = `${Math.round(eta)}s`;

        } else if (event.type === HttpEventType.Response) {
          fileWithProgress.progress.data.percentage = 100;
          fileWithProgress.uploading = false;
          fileWithProgress.uploaded = true;
          fileWithProgress.response = event.body;

          if (event.status === 200 || event.status === 201) {
            this.uploadedFiles.push(event.body);
            this.checkFiles();
          } else {
            this.toast.alert(this.translateService.instant("components.attachment.file-attachment.alert-files-requirements"));
          }
        }
      },
      error: (error) => {
        fileWithProgress.uploading = false;
        this.toast.alert(this.translateService.instant("components.attachment.file-attachment.alert-upload-error"));
        this.loading = false;
      }
    });
  }

  checkFiles(): void {
    const allUploaded = this.files.every(f => f.uploaded);
    if (allUploaded) {
      this.loading = false;
      this.attachElement.emit(this.uploadedFiles);
    }
  }

  cancelUpload(id: string): void {
    const index = this.files.findIndex(f => f.id === id);
    if (index !== -1) {
      this.files.splice(index, 1);

      // Check if we should emit the uploaded files
      if (this.files.length === 0 && this.uploadedFiles.length > 0) {
        this.loading = false;
        this.attachElement.emit(this.uploadedFiles);
      }
    }
  }

  removeAllFiles(): void {
    this.files = [];
    this.uploadedFiles = [];
    this.loading = false;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
